import React from "react";
import {
  Card,
  Typography,
  LinearProgress,
  Chip,
  Stack,
  IconButton,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box } from "@mui/system";
import EmojiObjectsOutlinedIcon from "@mui/icons-material/EmojiObjectsOutlined";
import {
  typography,
  spacing,
  colors,
  shadows,
} from "../../../../../../../../../assets/styles/tokens";

interface KpiCardProps {
  title: string;
  value: string;
  total: string;
  rr: string;
  rrColor: "success" | "warning" | "error";
  progress: number; // 0–100
  predicted: string;
  extraInfo?: string;
}

const formatExtraInfo = (text: string) => {
  return text
    .split(/(\$|\d+(?:\.\d{1,2})?)/)
    .map((part: string, index: number) =>
      /(\$|\d+(?:\.\d{1,2})?)/.test(part) ? (
        <Box
          key={index}
          component="span"
          sx={{
            fontWeight: typography.Font_weight.Semibold.$value,
            color: colors.GrayLightMode[500].$value,
          }}
        >
          {part !== "$" && !isNaN(Number(part))
            ? Number(part).toLocaleString()
            : part}
        </Box>
      ) : (
        part
      ),
    );
};

const buildShadow = (shadowToken: any) =>
  shadowToken.$value
    .map(
      (s: any) => `${s.offsetX} ${s.offsetY} ${s.blur} ${s.spread} ${s.color}`,
    )
    .join(", ");

export function HighLevelKpiCard({
  title,
  value,
  total,
  rr,
  rrColor,
  progress,
  predicted,
  extraInfo,
}: KpiCardProps) {
  const [open, setOpen] = React.useState(false);

  const progressColor =
    rrColor === "success"
      ? colors.Success[500].$value
      : rrColor === "warning"
        ? colors.Warning[500].$value
        : colors.Error[500].$value;

  return (
    <Card
      sx={{
        p: 1,
        borderRadius: spacing.Radius.lg.$value,
        boxShadow: buildShadow(shadows.Shadows.sm),
      }}
    >
      {/* Top Row */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="body2"
          sx={{
            color: colors.GrayLightMode[700].$value,
            fontWeight: typography.Font_weight.Medium.$value,
          }}
        >
          {title}
        </Typography>
      </Stack>

      {/* Main Value */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        mt={0.5}
      >
        <Typography
          variant="h5"
          sx={{
            mt: 1,
            fontWeight: typography.Font_weight.Semibold.$value,
            color: colors.GrayLightMode[900].$value,
          }}
        >
          {value}{" "}
          <Typography
            component="span"
            variant="body2"
            sx={{
              fontWeight: typography.Font_weight.Regular.$value,
              color: colors.GrayLightMode[900].$value,
            }}
          >
            out of{" "}
            <Box
              component="span"
              sx={{
                fontWeight: typography.Font_weight.Semibold.$value,
                color: colors.GrayLightMode[900].$value,
              }}
            >
              {total}
            </Box>
          </Typography>
        </Typography>
        <Stack
          direction="row"
          sx={{ gap: spacing.Spacing.xs.$value }}
          alignItems="center"
        >
          <Typography
            variant="body2"
            sx={{
              color: colors.GrayLightMode[900].$value,
            }}
          >
            RR
          </Typography>
          <Chip
            label={rr}
            size="small"
            sx={{
              bgcolor:
                rrColor === "success"
                  ? colors.Success[50].$value
                  : rrColor === "warning"
                    ? colors.Warning[50].$value
                    : colors.Error[50].$value,

              color:
                rrColor === "success"
                  ? colors.Success[600].$value
                  : rrColor === "warning"
                    ? colors.Warning[600].$value
                    : colors.Error[600].$value,

              fontWeight: typography.Font_weight.Semibold.$value,
              borderRadius: spacing.Radius.sm.$value,
            }}
          />
        </Stack>
      </Stack>

      {/* Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          mt: spacing.Spacing.xs.$value,
          height: spacing.Spacing.md.$value,
          borderRadius: spacing.Radius.full.$value,
          backgroundColor: colors.GrayLightMode[100].$value,

          "& .MuiLinearProgress-bar": {
            borderRadius: spacing.Radius.full.$value,
            backgroundColor: progressColor,
          },
        }}
      />

      {/* Predicted Row */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={1}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: typography.Font_weight.Regular.$value,
            color: colors.GrayLightMode[900].$value,
          }}
        >
          Predicted RR{" "}
          <Box
            component="span"
            sx={{
              fontWeight: typography.Font_weight.Semibold.$value,
              color: colors.GrayLightMode[900].$value,
            }}
          >
            {predicted}
          </Box>
        </Typography>

        {extraInfo && (
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            sx={{ padding: "0" }}
            data-tracker="646"
          >
            <ExpandMoreIcon
              sx={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "0.2s",
              }}
            />
          </IconButton>
        )}
      </Stack>

      {/* Expandable Info */}
      {extraInfo && (
        <Collapse in={open}>
          <Stack direction="row" spacing={1} alignItems="flex-start" mt={1}>
            <EmojiObjectsOutlinedIcon
              sx={{
                fontSize: typography.Size.Text.lg.$value,
                color: colors.GrayLightMode[500].$value,
              }}
            />

            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[500].$value,
                fontStyle: "italic", //need to discuss later with design
                lineHeight: typography.Line_height.text.xs.$value,
              }}
            >
              {formatExtraInfo(extraInfo)}
            </Typography>
          </Stack>
        </Collapse>
      )}
    </Card>
  );
}
